/**
 * MafiaPlayerList — Player list with alive/dead/disconnect indicators
 *
 * Requirements: 12.5, 27.1
 */

import React from 'react';
import { VisiblePlayer, ROLE_INFO } from '../types';
import './MafiaPlayerList.css';

export interface MafiaPlayerListProps {
  players: VisiblePlayer[];
  currentPlayerId?: string;
  showRoles?: boolean;
}

export const MafiaPlayerList: React.FC<MafiaPlayerListProps> = ({
  players,
  currentPlayerId,
  showRoles = false,
}) => {
  const alive = players.filter((p) => p.status === 'alive');
  const dead = players.filter((p) => p.status === 'dead');

  const renderPlayer = (player: VisiblePlayer) => {
    const isDead = player.status === 'dead';
    const isDisconnected = player.connectionStatus === 'disconnected';
    const isMe = player.id === currentPlayerId;
    const roleInfo = player.role ? ROLE_INFO[player.role] : null;

    return (
      <li
        key={player.id}
        className={`mafia-player-list__item ${isDead ? 'mafia-player-list__item--dead' : 'mafia-player-list__item--alive'}`}
      >
        <span className="mafia-player-list__name">
          <span className="mafia-player-list__status-icon">
            {isDead ? '👻' : isDisconnected ? '⚠️' : '🟢'}
          </span>
          {player.name}
          {isMe && ' (You)'}
          {player.isHost && ' 👑'}
        </span>
        <span>
          {(showRoles || isDead) && roleInfo && (
            <span className="mafia-player-list__role-tag">
              {roleInfo.icon} {roleInfo.name}
            </span>
          )}
          {isDisconnected && !isDead && (
            <span className="mafia-player-list__disconnect">Disconnected</span>
          )}
        </span>
      </li>
    );
  };

  return (
    <div className="mafia-player-list" role="region" aria-label="Player List">
      <h3 className="mafia-player-list__title">
        👥 Players ({alive.length} alive / {players.length} total)
      </h3>
      <ul className="mafia-player-list__items">
        {alive.map(renderPlayer)}
        {dead.map(renderPlayer)}
      </ul>
    </div>
  );
};

export default MafiaPlayerList;
